"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { RecoverPasswordFormSchema } from "./schema";
import { z } from "zod";
import { Form, FormMessage } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import hooks from "@/hooks";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useToast } from "@/hooks/use-toast";
import PhoneInput from "react-phone-input-2";
import { ptBRCountries } from "@/consts/PtBRCountries";

type Recover = z.infer<typeof RecoverPasswordFormSchema>;

interface IRecoverPasswordForm {
  phone: string & {};
}

const RecoverPasswordForm = ({ phone }: IRecoverPasswordForm) => {
  const t = useTranslations("Auth.RecoverPasswordPage.form");

  const router = useRouter();
  const { toast } = useToast();

  const [interval, setIntervalValue] = useState(0);

  const form = useForm<Recover>({
    resolver: zodResolver(RecoverPasswordFormSchema),
    defaultValues: {
      phone: phone,
      newPassword: "",
      confirmNewPassword: "",
      securityCode: "",
    },
  });

  const recoverService = hooks.useRecoverPassword({
    onSuccess: () => {
      toast({
        title: t("toasts.success.changePassword.title"),
        description: t("toasts.success.changePassword.description"),
        variant: "success",
      });
      handleRedirectToLogin();
    },
    onError: (error: unknown) => {
      toast({
        title: t("toasts.unexpectedError.changePassword.title"),
        description:
          (error as Error).message ??
          t("toasts.unexpectedError.changePassword.description"),
        variant: "warning",
      });
    },
  });

  const requestCodeService = hooks.useRequestCodeByPhone({
    onSuccess: () => {
      toast({
        title: t("toasts.success.resend.title"),
        description: t("toasts.success.resend.description"),
      });
    },
    onError: (err) => {
      toast({
        title: t("toasts.unexpectedError.resend.title"),
        description:
          err.message ?? t("toasts.unexpectedError.resend.description"),
        variant: "warning",
      });
    },
  });

  const handleResendOTP = async () => {
    await requestCodeService
      .mutateAsync({ phone: phone })
      .then(() => setIntervalValue(60));
  };

  function handleRedirectToLogin() {
    setTimeout(() => router.replace("/login"), 2000);
  }

  async function onSubmit(data: Recover) {
    await recoverService.mutateAsync(data);
  }

  useEffect(() => {
    if (interval > 0) {
      const timer = setInterval(() => {
        setIntervalValue((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [interval]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="md:p-8 w-full max-w-96 flex flex-col items-center gap-y-6"
      >
        <div className="flex flex-col w-full">
          <label htmlFor="whatsapp" className="text-sm font-semibold mb-1">
            {t("label.phone")}
          </label>
          <PhoneInput
            country={"br"}
            value={form.watch("phone")}
            onChange={(value) => {
              const numericValue = value.replace(/\D/g, "");
              form.setValue("phone", numericValue);
            }}
            disabled
            countryCodeEditable={false}
            localization={{ ...ptBRCountries }}
            inputStyle={{
              color: "rgb(133 131 116)",
              width: "100%",
              height: "40px",
              borderRadius: "9999px",
              border: "1px solid #e5e7eb",
              backgroundColor: "transparent",
              padding: "0.25rem 0.75rem",
              paddingLeft: "55px",
              fontSize: "1rem",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              transition: "all 0.2s",
              outline: "none",
            }}
            containerStyle={{ width: "100%" }}
            buttonStyle={{
              borderTopLeftRadius: "9999px",
              borderBottomLeftRadius: "9999px",
              width: "50px",
              border: "1px solid rgb(229, 231, 235)",
              paddingLeft: "6px",
            }}
            dropdownStyle={{
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
            inputProps={{
              name: "whatsapp",
              placeholder: t("placeholder.whatsapp"),
            }}
          />
          {form.formState.errors.phone && (
            <FormMessage className="mt-2">
              {form.formState.errors.phone.message}
            </FormMessage>
          )}
        </div>

        <FormFields.InputFormField
          form={form}
          formName="securityCode"
          label={t("label.securityCode")}
          placeholder={t("placeholder.securityCode")}
        />
        <FormFields.InputFormField
          form={form}
          formName="newPassword"
          label={t("label.newPassword")}
          placeholder={t("placeholder.newPassword")}
          type="password"
        />
        <FormFields.InputFormField
          form={form}
          formName="confirmNewPassword"
          label={t("label.confirmNewPassword")}
          placeholder={t("placeholder.confirmNewPassword")}
          type="password"
        />
        <Buttons.DefaultButton
          className="py-6"
          type="submit"
          title={t("button.changePassword")}
          isLoading={recoverService.isPending}
        />
        <Buttons.DefaultButton
          title={t("button.resend")}
          className="py-6"
          variant="secondary"
          type="button"
          onClick={handleResendOTP}
          isLoading={requestCodeService.isPending}
          disabled={interval > 0}
        />
        {interval > 0 && (
          <p className="text-paragraph-xs">
            <span>{t("resendText")}:</span>&nbsp;
            {interval === 60 ? (
              <span className="text-muted-foreground">01:00</span>
            ) : (
              <span className="text-muted-foreground">
                00:{interval < 10 ? `0${interval}` : interval}
              </span>
            )}
          </p>
        )}
      </form>
    </Form>
  );
};

export default RecoverPasswordForm;
